import { useEffect, useState } from "react";
import api, { fmtPKR, fmtDate, downloadFile } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Plus, Pencil, Trash2, Search, Download, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import PageShell from "../components/PageShell";

const emptyParty = {
  name: "",
  phone: "",
  address: "",
  type: "customer",
  opening_balance: 0,
};

const emptyEntry = {
  date: new Date().toISOString().slice(0, 10),
  description: "",
  reference_type: "payment",
  debit: 0,
  credit: 0,
};

export default function PartyLedger() {
  const [parties, setParties] = useState([]);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyParty);
  const [editingId, setEditingId] = useState(null);

  const [selectedParty, setSelectedParty] = useState(null);
  const [ledger, setLedger] = useState(null);

  const [entryDialog, setEntryDialog] = useState(false);
  const [entryForm, setEntryForm] = useState(emptyEntry);
  const [editingEntryId, setEditingEntryId] = useState(null);

  const load = async () => {
    const res = await api.get("/parties", {
      params: { search: search || undefined, type: typeFilter !== "all" ? typeFilter : undefined },
    });
    setParties(res.data);
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search, typeFilter]);

  const loadLedger = async (id) => {
    const res = await api.get(`/parties/${id}/ledger`);
    setLedger(res.data);
  };

  const openParty = async (p) => {
    setSelectedParty(p);
    await loadLedger(p.id);
  };

  const openCreate = () => {
    setForm(emptyParty);
    setEditingId(null);
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setForm({
      name: p.name,
      phone: p.phone || "",
      address: p.address || "",
      type: p.type,
      opening_balance: p.opening_balance || 0,
    });
    setEditingId(p.id);
    setDialogOpen(true);
  };

  const saveParty = async () => {
    if (!form.name) return toast.error("Name required");
    try {
      if (editingId) {
        await api.put(`/parties/${editingId}`, {
          ...form,
          opening_balance: Number(form.opening_balance) || 0,
        });
        toast.success("Updated");
      } else {
        await api.post("/parties", {
          ...form,
          opening_balance: Number(form.opening_balance) || 0,
        });
        toast.success("Added");
      }
      setDialogOpen(false);
      await load();
      if (selectedParty && editingId === selectedParty.id) {
        const updated = (await api.get("/parties")).data.find((x) => x.id === editingId);
        if (updated) {
          setSelectedParty(updated);
          await loadLedger(updated.id);
        }
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const delParty = async (p) => {
    if (!window.confirm(`Delete "${p.name}" and all ledger entries?`)) return;
    try {
      await api.delete(`/parties/${p.id}`);
      toast.success("Deleted");
      if (selectedParty?.id === p.id) {
        setSelectedParty(null);
        setLedger(null);
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const openEntryCreate = () => {
    setEntryForm(emptyEntry);
    setEditingEntryId(null);
    setEntryDialog(true);
  };

  const openEntryEdit = (e) => {
    setEntryForm({
      date: e.date?.slice(0, 10) || new Date().toISOString().slice(0, 10),
      description: e.description,
      reference_type: e.reference_type,
      debit: e.debit || 0,
      credit: e.credit || 0,
    });
    setEditingEntryId(e.id);
    setEntryDialog(true);
  };

  const saveEntry = async () => {
    if (!entryForm.description) return toast.error("Description required");
    try {
      const payload = {
        party_id: selectedParty.id,
        date: new Date(entryForm.date).toISOString(),
        description: entryForm.description,
        reference_type: entryForm.reference_type,
        debit: Number(entryForm.debit) || 0,
        credit: Number(entryForm.credit) || 0,
      };
      if (editingEntryId) {
        await api.put(`/ledger/${editingEntryId}`, {
          date: payload.date,
          description: payload.description,
          debit: payload.debit,
          credit: payload.credit,
        });
        toast.success("Entry updated");
      } else {
        await api.post("/ledger", payload);
        toast.success("Entry added");
      }
      setEntryDialog(false);
      await loadLedger(selectedParty.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const delEntry = async (e) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await api.delete(`/ledger/${e.id}`);
      toast.success("Deleted");
      await loadLedger(selectedParty.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const exportLedger = async () => {
    if (!selectedParty) return;
    try {
      await downloadFile(
        `/export/ledger/${selectedParty.id}`,
        `ledger_${selectedParty.name}.xlsx`
      );
    } catch {
      toast.error("Export failed");
    }
  };

  // List view (no party selected)
  if (!selectedParty) {
    const customerCount = parties.filter((p) => p.type === "customer").length;
    const supplierCount = parties.filter((p) => p.type === "supplier").length;
    return (
      <PageShell
        ticker={[
          { label: "Console", value: "PARTY LEDGER", live: true },
          { label: "Customers", value: String(customerCount).padStart(3, "0"), mono: true },
          { label: "Suppliers", value: String(supplierCount).padStart(3, "0"), mono: true },
          { label: "Currency", value: "PKR ₨" },
        ]}
        eyebrow="Chapter · Parties"
        title={<>Customers &amp; <em className="italic text-blue-700">suppliers</em></>}
        description="Balances, debits and credits — a running tab for every person who walks in."
        colophon="Party Ledger"
        cta={
          <Button
            onClick={openCreate}
            className="bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="ledger-add-party-btn"
          >
            <Plus size={16} className="mr-2" /> Add Party
          </Button>
        }
      >
        <div data-testid="ledger-page">
        <div className="flex gap-3 mb-4 flex-wrap">
          <div className="relative flex-1 min-w-[260px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, phone…"
              data-testid="ledger-search-input"
              className="pl-9"
            />
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-48" data-testid="ledger-type-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Parties</SelectItem>
              <SelectItem value="customer">Customers</SelectItem>
              <SelectItem value="supplier">Suppliers</SelectItem>
              <SelectItem value="both">Both (dual role)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Card className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-left">Type</th>
                <th className="px-4 py-3 text-left">Phone</th>
                <th className="px-4 py-3 text-left">Address</th>
                <th className="px-4 py-3 text-right">Opening Balance</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {parties.map((p) => (
                <tr
                  key={p.id}
                  className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                  onClick={() => openParty(p)}
                  data-testid={`party-row-${p.id}`}
                >
                  <td className="px-4 py-2 text-slate-900 font-medium">{p.name}</td>
                  <td className="px-4 py-2">
                    <span
                      className={`text-xs px-2 py-0.5 rounded uppercase tracking-wider ${
                        p.type === "customer"
                          ? "bg-blue-50 text-blue-700"
                          : p.type === "supplier"
                          ? "bg-amber-50 text-amber-700"
                          : "bg-violet-50 text-violet-700"
                      }`}
                    >
                      {p.type === "both" ? "customer & supplier" : p.type}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-slate-600">{p.phone || "-"}</td>
                  <td className="px-4 py-2 text-slate-500 text-xs truncate max-w-xs">{p.address || "-"}</td>
                  <td className="px-4 py-2 text-right tabular text-slate-900">
                    {fmtPKR(p.opening_balance)}
                  </td>
                  <td className="px-4 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => openEdit(p)}
                      className="text-slate-500 hover:text-blue-700 mr-3"
                      data-testid={`party-edit-${p.id}`}
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      onClick={() => delParty(p)}
                      className="text-slate-500 hover:text-red-700"
                      data-testid={`party-delete-${p.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
              {parties.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-slate-400 text-sm">
                    No parties yet. Click "Add Party" to begin.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Card>

        {/* Party dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingId ? "Edit Party" : "Add Party"}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Name *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="party-name-input"
                />
              </div>
              <div>
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="party-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="customer">Customer</SelectItem>
                    <SelectItem value="supplier">Supplier</SelectItem>
                    <SelectItem value="both">Both (Customer &amp; Supplier)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  data-testid="party-phone-input"
                />
              </div>
              <div className="col-span-2">
                <Label>Address</Label>
                <Input
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  data-testid="party-address-input"
                />
              </div>
              <div className="col-span-2">
                <Label>Opening Balance (₨)</Label>
                <Input
                  type="number"
                  value={form.opening_balance}
                  onChange={(e) => setForm({ ...form, opening_balance: e.target.value })}
                  data-testid="party-balance-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Positive = party owes you. Negative = you owe party.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={saveParty} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="party-save-btn">
                {editingId ? "Update" : "Save"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      </PageShell>
    );
  }

  // Detail view
  return (
    <PageShell
      ticker={[
        { label: "Console", value: "LEDGER", live: true },
        { label: "Party", value: selectedParty.name.toUpperCase().slice(0, 24) },
        {
          label: "Balance",
          value: (ledger?.balance || 0) > 0 ? "DEBIT" : (ledger?.balance || 0) < 0 ? "CREDIT" : "SETTLED",
          mono: true,
        },
        { label: "Entries", value: String(ledger?.entries?.length || 0).padStart(3, "0"), mono: true },
      ]}
      eyebrow={`${selectedParty.type === "customer" ? "Customer" : selectedParty.type === "supplier" ? "Supplier" : "Customer & Supplier"} · Detailed ledger`}
      title={<>{selectedParty.name}</>}
      description={`${selectedParty.phone || "no phone"} · ${selectedParty.address || "no address"}`}
      colophon={`Ledger · ${selectedParty.name}`}
      cta={
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={exportLedger} data-testid="ledger-export-btn">
            <Download size={16} className="mr-2" /> Export Excel
          </Button>
          <Button
            onClick={openEntryCreate}
            className="bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="ledger-add-entry-btn"
          >
            <Plus size={16} className="mr-2" /> Add Entry
          </Button>
        </div>
      }
    >
      <div data-testid="ledger-detail-page">
      <button
        onClick={() => {
          setSelectedParty(null);
          setLedger(null);
        }}
        data-testid="ledger-back-btn"
        className="text-xs uppercase tracking-[0.24em] text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 mb-6"
      >
        <ArrowLeft size={14} /> Back to parties
      </button>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <Card className="p-4 bg-white border border-slate-200">
          <div className="text-xs uppercase tracking-wider text-slate-500">Opening Balance</div>
          <div className="font-heading text-xl font-bold tabular mt-1">
            {fmtPKR(selectedParty.opening_balance)}
          </div>
        </Card>
        <Card className="p-4 bg-white border border-slate-200">
          <div className="text-xs uppercase tracking-wider text-slate-500">Total Entries</div>
          <div className="font-heading text-xl font-bold tabular mt-1">
            {ledger?.entries?.length || 0}
          </div>
        </Card>
        <Card className="p-4 bg-white border border-slate-200">
          <div className="text-xs uppercase tracking-wider text-slate-500">Current Balance</div>
          <div
            className={`font-heading text-xl font-bold tabular mt-1 ${
              (ledger?.balance || 0) > 0
                ? "text-red-700"
                : (ledger?.balance || 0) < 0
                ? "text-emerald-700"
                : "text-slate-900"
            }`}
          >
            {fmtPKR(ledger?.balance || 0)}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {(ledger?.balance || 0) > 0
              ? "Party owes you"
              : (ledger?.balance || 0) < 0
              ? "You owe party"
              : "Settled"}
          </div>
        </Card>
      </div>

      <Card className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-xs uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3 text-left">Date</th>
              <th className="px-4 py-3 text-left">Description</th>
              <th className="px-4 py-3 text-left">Type</th>
              <th className="px-4 py-3 text-right">Debit</th>
              <th className="px-4 py-3 text-right">Credit</th>
              <th className="px-4 py-3 text-right">Balance</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(ledger?.entries || []).map((e) => (
              <tr key={e.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`ledger-entry-${e.id}`}>
                <td className="px-4 py-2 text-slate-600 text-xs">{fmtDate(e.date)}</td>
                <td className="px-4 py-2 text-slate-900">{e.description}</td>
                <td className="px-4 py-2 text-xs uppercase tracking-wider text-slate-500">
                  {e.reference_type}
                </td>
                <td className="px-4 py-2 text-right tabular text-red-700">
                  {e.debit ? fmtPKR(e.debit) : "-"}
                </td>
                <td className="px-4 py-2 text-right tabular text-emerald-700">
                  {e.credit ? fmtPKR(e.credit) : "-"}
                </td>
                <td className="px-4 py-2 text-right tabular font-medium">
                  {fmtPKR(e.balance || 0)}
                </td>
                <td className="px-4 py-2 text-right">
                  {!["opening", "sale"].includes(e.reference_type) && (
                    <>
                      <button
                        onClick={() => openEntryEdit(e)}
                        className="text-slate-500 hover:text-blue-700 mr-3"
                        data-testid={`ledger-edit-${e.id}`}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => delEntry(e)}
                        className="text-slate-500 hover:text-red-700"
                        data-testid={`ledger-del-${e.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {(ledger?.entries || []).length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-slate-400 text-sm">
                  No entries. Sales and payments will appear here.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>

      <Dialog open={entryDialog} onOpenChange={setEntryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingEntryId ? "Edit Entry" : "Add Ledger Entry"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                value={entryForm.date}
                onChange={(e) => setEntryForm({ ...entryForm, date: e.target.value })}
                data-testid="entry-date-input"
              />
            </div>
            <div>
              <Label>Type</Label>
              <Select
                value={entryForm.reference_type}
                onValueChange={(v) => setEntryForm({ ...entryForm, reference_type: v })}
                disabled={!!editingEntryId}
              >
                <SelectTrigger data-testid="entry-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="payment">Payment Made</SelectItem>
                  <SelectItem value="receipt">Payment Received</SelectItem>
                  <SelectItem value="purchase">Purchase</SelectItem>
                  <SelectItem value="adjustment">Adjustment</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Description</Label>
              <Input
                value={entryForm.description}
                onChange={(e) => setEntryForm({ ...entryForm, description: e.target.value })}
                data-testid="entry-desc-input"
              />
            </div>
            <div>
              <Label>Debit (₨)</Label>
              <Input
                type="number"
                value={entryForm.debit}
                onChange={(e) => setEntryForm({ ...entryForm, debit: e.target.value })}
                data-testid="entry-debit-input"
              />
              <p className="text-xs text-slate-500 mt-1">↑ Increases party balance</p>
            </div>
            <div>
              <Label>Credit (₨)</Label>
              <Input
                type="number"
                value={entryForm.credit}
                onChange={(e) => setEntryForm({ ...entryForm, credit: e.target.value })}
                data-testid="entry-credit-input"
              />
              <p className="text-xs text-slate-500 mt-1">↓ Decreases party balance</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEntryDialog(false)}>
              Cancel
            </Button>
            <Button onClick={saveEntry} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="entry-save-btn">
              {editingEntryId ? "Update" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </PageShell>
  );
}
